import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';
import type { SupportUserRef } from '@/lib/api';
import { AdminLoadError } from '../components/AdminLoadError';
import { UserIdentity } from '../components/UserIdentity';
import { StatusFilterCheckboxes } from '../components/StatusFilterCheckboxes';
import { ArcheChip } from '../components/ArcheChip';
import { ArcheFilterSelect } from '../components/ArcheFilterSelect';
import { SupportRefId, AwaitingReplyFlag, ReplyWaivedCheckbox } from '../components';
import { useReplyWaiver } from '../hooks/useReplyWaiver';
import { useStatusFilterParam } from '../hooks/useStatusFilterParam';
import { useSessionPersistedSearchParams } from '../hooks/useSessionPersistedSearchParams';
import { useSupportUnreadCounts } from '../hooks/useSupportUnreadCounts';
import { useArcheDisplayMap } from '../hooks/useArcheDisplayMap';
import { useArcheFilterParam } from '../hooks/useArcheFilterParam';

interface Suggestion {
  suggestionId: string;
  userId: string;
  user?: SupportUserRef | null;
  unread?: boolean;
  awaitingReply?: boolean;
  replyWaived?: boolean;
  archeId?: string | null;
  title: string;
  description: string;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  {
    value: 'reviewed',
    label: 'Reviewed',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  { value: 'planned', label: 'Planned', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-800 border-gray-200' },
];

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge variant="outline" className={config.color}>
      {config.label}
    </Badge>
  );
}

// Remember the suggestions filter view (status + arche) for the session, so it
// survives opening a suggestion and returning via the bare-route back link.
const VIEW_STORAGE_KEY = 'support-admin-suggestions-view';
const VIEW_PARAM_KEYS = ['status', 'arche'] as const;

export function AdminSuggestionsPage() {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { statuses, setStatuses } = useStatusFilterParam();
  const { arche, setArche } = useArcheFilterParam();
  // Side-effect only: persist/restore the view above (written via those hooks).
  useSessionPersistedSearchParams(VIEW_STORAGE_KEY, VIEW_PARAM_KEYS);
  const { refresh: refreshUnread } = useSupportUnreadCounts();
  const { metaById: archeMeta } = useArcheDisplayMap(suggestions.map((s) => s.archeId));

  // Edit dialog
  const [editSuggestion, setEditSuggestion] = useState<Suggestion | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const replyWaiver = useReplyWaiver('suggestions');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteSuggestion, setDeleteSuggestion] = useState<Suggestion | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await api.listSuggestionsAdmin({
        status: statuses.length ? statuses : undefined,
        archeId: arche || undefined,
        limit: 50,
      });
      setSuggestions(result.suggestions);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [statuses, arche]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleEdit = (suggestion: Suggestion) => {
    setEditSuggestion(suggestion);
    setEditStatus(suggestion.status);
    setEditNotes(suggestion.adminNotes || '');
    replyWaiver.reset(suggestion.replyWaived);
    // Opening the item marks it read (optimistic local clear + server stamp).
    if (suggestion.unread) {
      setSuggestions((prev) =>
        prev.map((s) => (s.suggestionId === suggestion.suggestionId ? { ...s, unread: false } : s)),
      );
      api
        .markSuggestionReadAdmin(suggestion.suggestionId)
        .then(() => refreshUnread())
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!editSuggestion) return;
    setSaving(true);
    try {
      // Waiver first: if it fails, nothing has been committed yet.
      await replyWaiver.save(editSuggestion.suggestionId);
      await api.updateSuggestionAdmin(editSuggestion.suggestionId, {
        status: editStatus,
        adminNotes: editNotes.trim() || null,
      });
      setEditSuggestion(null);
      await fetchSuggestions();
    } catch (err) {
      console.error('Failed to update suggestion:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteSuggestion) return;
    setDeleting(true);
    try {
      await api.deleteSuggestionAdmin(deleteSuggestion.suggestionId);
      setDeleteSuggestion(null);
      await fetchSuggestions();
    } catch (err) {
      console.error('Failed to delete suggestion:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Feature Suggestions</h2>
          <p className="text-sm text-muted-foreground">
            {total} suggestion{total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <StatusFilterCheckboxes
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={setStatuses}
        />
        <ArcheFilterSelect value={arche} onChange={setArche} />
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <AdminLoadError what="suggestions" onRetry={fetchSuggestions} />
      ) : suggestions.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No suggestions found.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36px]">
                  <span className="sr-only">Awaiting reply</span>
                </TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="w-[160px]">Suggestion ID</TableHead>
                <TableHead className="w-[160px]">Customer</TableHead>
                <TableHead className="w-[150px]">Arche</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suggestions.map((s) => (
                <TableRow
                  key={s.suggestionId}
                  className="cursor-pointer"
                  onClick={() => handleEdit(s)}
                >
                  <TableCell>
                    <AwaitingReplyFlag awaiting={s.awaitingReply} />
                  </TableCell>
                  <TableCell>
                    <p className="flex items-center gap-1.5 text-sm truncate max-w-[400px]">
                      {s.unread && (
                        <span
                          className="h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                          title="Unread"
                        />
                      )}
                      <span className={s.unread ? 'font-semibold' : 'font-medium'}>{s.title}</span>
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 max-w-[400px]">
                      {s.description}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <SupportRefId id={s.suggestionId} label="Suggestion ID" />
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <UserIdentity user={s.user} userId={s.userId} />
                  </TableCell>
                  <TableCell>
                    <ArcheChip
                      archeId={s.archeId}
                      meta={s.archeId ? archeMeta.get(s.archeId) : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteSuggestion(s);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editSuggestion} onOpenChange={(open) => !open && setEditSuggestion(null)}>
        <DialogContent>
          {editSuggestion && (
            <>
              <DialogHeader>
                <DialogTitle>{editSuggestion.title}</DialogTitle>
                <SupportRefId
                  id={editSuggestion.suggestionId}
                  label="Suggestion ID"
                  variant="full"
                  className="-ml-1 self-start"
                />
              </DialogHeader>
              <div className="rounded-md bg-muted p-3 max-h-[200px] overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">{editSuggestion.description}</p>
              </div>
              {editSuggestion.adminNotes && (
                <div className="rounded-md bg-muted/50 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Previous admin notes:
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{editSuggestion.adminNotes}</p>
                </div>
              )}
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Admin Notes</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Internal notes about this suggestion..."
                    rows={3}
                  />
                </div>
              </div>
              <ReplyWaivedCheckbox
                waived={replyWaiver.waived}
                onChange={replyWaiver.setWaived}
                disabled={saving}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditSuggestion(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteSuggestion} onOpenChange={(open) => !open && setDeleteSuggestion(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Suggestion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteSuggestion?.title}&rdquo;? This cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSuggestion(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
